import { useTranslations } from '@vocab/react';
import {
  Button,
  Dialog,
  Inline,
  Stack,
  Strong,
  Text,
} from 'braid-design-system';
import { useId, type FC } from 'react';

import type { TrackingCurrentPage } from 'src/constants/pageTrackingData';
import { TrackOnMount } from 'src/libs/Analytics';
import {
  ConnectionPlugIllustration,
  SuccessTick,
} from 'src/libs/Illustrations';

import translations from './.vocab';
import { useOrderReferenceCheckMutation } from './OrderReferenceCheckDialog.hirer.generated';

type TrackingFnProps = {
  currentPage: TrackingCurrentPage;
  candidateId: number;
  prospectId: string;
  applicationCorrelationId: string;
};

interface OrderReferenceCheckDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: number;
  referenceCreditBalance: number;
  candidateDetails: {
    prospectId: string;
    candidateFirstName: string;
    candidateFullName: string;
    applicationCorrelationId: string;
    currentPage: TrackingCurrentPage;
    candidateId: number;
  };
  onCompleted: (props: { applicationCorrelationId: string }) => void;
  trackOrderReferenceCheckDialogDisplayedEvent: ({
    ...TrackingFnProps
  }: TrackingFnProps) => void;
  trackOrderReferenceCheckDialogDismissedEvent: ({
    ...TrackingFnProps
  }: TrackingFnProps) => void;
  trackOrderReferenceCheckPressedEvent: ({
    ...TrackingFnProps
  }: TrackingFnProps) => void;
  trackOrderReferenceCheckErrorDialogDisplayedEvent: (
    currentPage: TrackingFnProps['currentPage'],
  ) => void;
  trackOrderReferenceCheckErrorDialogDismissedEvent: (
    currentPage: TrackingFnProps['currentPage'],
  ) => void;
  trackOrderReferenceCheckSuccessDialogDisplayedEvent: ({
    ...TrackingFnProps
  }: TrackingFnProps) => void;
  trackOrderReferenceCheckSuccessDialogDismissedEvent: ({
    ...TrackingFnProps
  }: TrackingFnProps) => void;
  budgetType: 'STANDARD' | 'LIMITED';
}

export const OrderReferenceCheckDialog: FC<OrderReferenceCheckDialogProps> = ({
  open,
  onClose,
  candidateDetails,
  jobId,
  referenceCreditBalance,
  onCompleted,
  trackOrderReferenceCheckDialogDisplayedEvent,
  trackOrderReferenceCheckDialogDismissedEvent,
  trackOrderReferenceCheckPressedEvent,
  trackOrderReferenceCheckErrorDialogDisplayedEvent,
  trackOrderReferenceCheckErrorDialogDismissedEvent,
  trackOrderReferenceCheckSuccessDialogDisplayedEvent,
  trackOrderReferenceCheckSuccessDialogDismissedEvent,
  budgetType,
}) => {
  const { t } = useTranslations(translations);

  const id = useId();
  const [
    orderReferenceCheck,
    {
      loading: referenceCheckMutationLoading,
      reset,
      data: referenceCheckData,
      error: referenceCheckError,
    },
  ] = useOrderReferenceCheckMutation();

  const handleOnClose = () => {
    trackOrderReferenceCheckDialogDismissedEvent({
      currentPage: candidateDetails.currentPage,
      candidateId: candidateDetails.candidateId,
      prospectId: candidateDetails.prospectId,
      applicationCorrelationId: candidateDetails.applicationCorrelationId,
    });
    onClose();
  };

  const handleOrderReferenceCheck = () => {
    trackOrderReferenceCheckPressedEvent({
      currentPage: candidateDetails.currentPage,
      candidateId: candidateDetails.candidateId,
      prospectId: candidateDetails.prospectId,
      applicationCorrelationId: candidateDetails.applicationCorrelationId,
    });

    orderReferenceCheck({
      variables: {
        input: {
          jobId,
          applicationCorrelationId: candidateDetails.applicationCorrelationId,
        },
      },
      onCompleted: (result) => {
        if (
          result.orderReferenceCheck.__typename ===
            'OrderReferenceCheckResponseSuccess' &&
          result.orderReferenceCheck.success
        ) {
          onCompleted({
            applicationCorrelationId: candidateDetails.applicationCorrelationId,
          });
        }
      },
    });
  };

  const onCloseError = () => {
    reset();
    trackOrderReferenceCheckErrorDialogDismissedEvent(
      candidateDetails.currentPage,
    );
    onClose();
  };

  const onCloseSuccess = () => {
    reset();
    trackOrderReferenceCheckSuccessDialogDismissedEvent({
      currentPage: candidateDetails.currentPage,
      candidateId: candidateDetails.candidateId,
      prospectId: candidateDetails.prospectId,
      applicationCorrelationId: candidateDetails.applicationCorrelationId,
    });
    onClose();
  };

  if (
    referenceCheckData?.orderReferenceCheck.__typename ===
      'OrderReferenceCheckResponseSuccess' &&
    referenceCheckData.orderReferenceCheck.success
  ) {
    return (
      <TrackOnMount
        onMount={() =>
          trackOrderReferenceCheckSuccessDialogDisplayedEvent({
            currentPage: candidateDetails.currentPage,
            candidateId: candidateDetails.candidateId,
            prospectId: candidateDetails.prospectId,
            applicationCorrelationId: candidateDetails.applicationCorrelationId,
          })
        }
      >
        <Dialog
          id={id}
          open
          closeLabel={t('Close')}
          onClose={onCloseSuccess}
          title={t('Reference check requested')}
          illustration={<SuccessTick width={128} height={128} />}
        >
          <Stack space="large">
            <Text align="center">
              {t(
                '<Strong>{firstName}</Strong> will now be asked to nominate 2 referees. Each time a referee completes their reference check, you will be notified by email.',
                {
                  firstName: candidateDetails.candidateFirstName,
                  Strong: (children) => <Strong>{children}</Strong>,
                },
              )}
            </Text>
            <Inline align="center" space="small" collapseBelow="tablet">
              <Button
                onClick={onCloseSuccess}
                tone="brandAccent"
                variant="solid"
              >
                {t('Close')}
              </Button>
            </Inline>
          </Stack>
        </Dialog>
      </TrackOnMount>
    );
  }

  if (
    referenceCheckError ||
    referenceCheckData?.orderReferenceCheck.__typename === 'ResponseError' ||
    (referenceCheckData?.orderReferenceCheck.__typename ===
      'OrderReferenceCheckResponseSuccess' &&
      !referenceCheckData.orderReferenceCheck.success)
  ) {
    return (
      <TrackOnMount
        onMount={() =>
          trackOrderReferenceCheckErrorDialogDisplayedEvent(
            candidateDetails.currentPage,
          )
        }
      >
        <Dialog
          id={id}
          open
          onClose={onCloseError}
          title={t("We couldn't check references for {firstName}", {
            firstName: candidateDetails.candidateFirstName,
          })}
          illustration={<ConnectionPlugIllustration height={128} width={128} />}
        >
          <Stack space="large" align="center">
            <Text>{t('Please refresh the page or try again later.')}</Text>
            <Inline space="small">
              <Button onClick={onCloseError} variant="solid" tone="brandAccent">
                {t('Close')}
              </Button>
            </Inline>
          </Stack>
        </Dialog>
      </TrackOnMount>
    );
  }

  return (
    <Dialog
      id={id}
      title={t('Check references for {candidateFirstName}?', {
        candidateFirstName: candidateDetails.candidateFirstName,
      })}
      open={open}
      onClose={handleOnClose}
      closeLabel={t('Close')}
    >
      <TrackOnMount
        onMount={() =>
          trackOrderReferenceCheckDialogDisplayedEvent({
            currentPage: candidateDetails.currentPage,
            candidateId: candidateDetails.candidateId,
            prospectId: candidateDetails.prospectId,
            applicationCorrelationId: candidateDetails.applicationCorrelationId,
          })
        }
      >
        <Stack space={['large', 'xlarge']}>
          <Stack space="large">
            <Text>
              {t(
                'Remember to let {candidateFullName} know they will receive an email from us to nominate their referees.',
                {
                  candidateFullName: candidateDetails.candidateFullName,
                },
              )}
            </Text>
            {budgetType === 'STANDARD' ? (
              <Text>
                {referenceCreditBalance === 1
                  ? t(
                      "You'll be using your last available reference check and won't be able to check references for other applicants.",
                    )
                  : t(
                      "You'll be able to check references for <Strong>{referencesRemaining} more applicant/s</Strong> after this.",
                      {
                        referencesRemaining: referenceCreditBalance - 1,
                        Strong: (children) => <Strong>{children}</Strong>,
                      },
                    )}
              </Text>
            ) : (
              <Text>
                {t(
                  "You're requesting a limited reference check. To view the full report, you can buy the add-on.",
                )}
              </Text>
            )}
          </Stack>
          <Inline space="medium" align="left" collapseBelow="tablet" reverse>
            <Button
              variant="solid"
              tone="brandAccent"
              onClick={handleOrderReferenceCheck}
              loading={referenceCheckMutationLoading}
            >
              {t('Check references')}
            </Button>

            <Button variant="soft" onClick={handleOnClose}>
              {t('Cancel')}
            </Button>
          </Inline>
        </Stack>
      </TrackOnMount>
    </Dialog>
  );
};
